import { type AutomergeUrl } from "@automerge/automerge-repo";
/**
 * The ```sketch code-block preview: a live, read-only view of a project
 * `.tldr` inside a markdown doc (floorplan 2.0's embed). The fence body is a
 * path plus optional params:
 *
 * ```sketch
 * path: plans/level-0.tldr
 * height: 360
 * ```
 *
 * A bare single-line body is treated as the path. The geometry stays in the
 * sketch doc; the fence is only a reference, so spec diffs stay readable and
 * the embed is always current.
 */
import { useDocHandle } from "@automerge/react";
import { RepoContext } from "@automerge/react";
import { useAutomergeStore } from "automerge-tldraw";
import type {
  CodeBlockPreview,
  CodeBlockPreviews,
} from "forest/markdown-editor/code-block";
import { Suspense, useEffect, useMemo, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import { Tldraw, type TLStoreSnapshot } from "tldraw";

import { repo } from "@/lib/automerge-repo";
import { CalloutBindingUtil } from "@/sketch/CalloutBindingUtil";
import { CalloutShapeUtil } from "@/sketch/CalloutShapeUtil";
import { DimBindingUtil } from "@/sketch/DimBindingUtil";
import { DimShapeUtil } from "@/sketch/DimShapeUtil";
import { GridlineShapeUtil } from "@/sketch/GridlineShapeUtil";
import { SKETCH_ASSET_URLS } from "@/sketch/sketch-assets";

import "tldraw/tldraw.css";

const EMBED_SHAPE_UTILS = [GridlineShapeUtil, DimShapeUtil, CalloutShapeUtil];
const EMBED_BINDING_UTILS = [DimBindingUtil, CalloutBindingUtil];
const DEFAULT_HEIGHT = 360;

export interface SketchFenceParams {
  path: string | null;
  height: number;
}

export function parseSketchFence(source: string): SketchFenceParams {
  const lines = source
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const params: SketchFenceParams = { path: null, height: DEFAULT_HEIGHT };
  if (lines.length === 1 && !lines[0]!.includes(":")) {
    params.path = lines[0]!;
    return params;
  }
  for (const line of lines) {
    const colon = line.indexOf(":");
    if (colon < 0) continue;
    const key = line.slice(0, colon).trim();
    const value = line.slice(colon + 1).trim();
    if (key === "path") params.path = value;
    else if (key === "height") {
      const parsed = Number(value);
      if (Number.isFinite(parsed) && parsed > 80) params.height = parsed;
    }
  }
  return params;
}

interface SketchEmbedHost {
  /** Resolve a project path to its Automerge url (null when missing). */
  resolvePath(path: string): Promise<AutomergeUrl | null>;
  openFile(path: string): void;
}

// Lucide pencil-ruler, inlined: the control strip is a vanilla NodeView.
const PENCIL_RULER_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 7 8.7 2.7a2.41 2.41 0 0 0-3.4 0L2.7 5.3a2.41 2.41 0 0 0 0 3.4L7 13"/><path d="m8 6 2-2"/><path d="m18 16 2-2"/><path d="m17 11 4.3 4.3c.94.94.94 2.46 0 3.4l-2.6 2.6c-.94.94-2.46.94-3.4 0L11 17"/><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/></svg>';

function SketchEmbedPreview({
  source,
  host,
}: {
  source: string;
  host: SketchEmbedHost;
}) {
  const params = useMemo(() => parseSketchFence(source), [source]);
  const [url, setUrl] = useState<AutomergeUrl | null | "pending">("pending");

  useEffect(() => {
    let cancelled = false;
    if (!params.path) {
      setUrl(null);
      return;
    }
    host.resolvePath(params.path).then((resolved) => {
      if (!cancelled) setUrl(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [params.path, host]);

  if (!params.path) {
    return (
      <div className="text-sm text-muted-foreground p-3">
        sketch embed: add <code>path: plans/site.tldr</code>
      </div>
    );
  }
  if (url === null) {
    return (
      <div className="text-sm text-muted-foreground p-3">
        {`Sketch "${params.path}" not found.`}
      </div>
    );
  }

  return (
    <div className="relative w-full" style={{ height: params.height }}>
      {url !== "pending" && (
        <RepoContext.Provider value={repo}>
          <Suspense fallback={null}>
            <SketchEmbedCanvas url={url} />
          </Suspense>
        </RepoContext.Provider>
      )}
    </div>
  );
}

function SketchEmbedCanvas({ url }: { url: AutomergeUrl }) {
  const handle = useDocHandle<TLStoreSnapshot>(url, { suspense: true });
  const store = useAutomergeStore({
    handle,
    shapeUtils: EMBED_SHAPE_UTILS,
    bindingUtils: EMBED_BINDING_UTILS,
  });
  return (
    <Tldraw
      licenseKey={import.meta.env.VITE_TLDRAW_LICENSE_KEY}
      store={store}
      assetUrls={SKETCH_ASSET_URLS}
      colorScheme="system"
      hideUi
      autoFocus={false}
      shapeUtils={EMBED_SHAPE_UTILS}
      bindingUtils={EMBED_BINDING_UTILS}
      onMount={(editor) => {
        editor.updateInstanceState({ isReadonly: true });
        const refit = () => {
          editor.setCameraOptions({ isLocked: false });
          editor.zoomToFit({ animation: { duration: 0 } });
          // A live picture, not a nested editor: the doc scrolls, not the
          // camera. Opening the sketch is what the button is for.
          editor.setCameraOptions({ isLocked: true });
        };
        refit();
        // The sketch is live; a peer drawing past the original bounds would
        // otherwise be clipped forever behind the locked camera.
        let timer: ReturnType<typeof setTimeout> | undefined;
        const unlisten = editor.store.listen(
          () => {
            clearTimeout(timer);
            timer = setTimeout(refit, 300);
          },
          { scope: "document", source: "remote" },
        );
        return () => {
          clearTimeout(timer);
          unlisten();
        };
      }}
    />
  );
}

const roots = new WeakMap<Node, Root>();

/** The `sketch` fence preview, for merging into a DocPane's preview map. */
export function makeSketchCodePreview(host: SketchEmbedHost): CodeBlockPreview {
  return {
    render({ source }) {
      const container = document.createElement("div");
      container.className = "w-full";
      const root = createRoot(container);
      roots.set(container, root);
      // Mount off the editor's render pass (mirror of the unmount microtask),
      // so this root's effects don't race the tree that owns the node.
      queueMicrotask(() =>
        root.render(<SketchEmbedPreview source={source} host={host} />),
      );
      return container;
    },
    destroy(node) {
      const root = roots.get(node);
      if (!root) return;
      roots.delete(node);
      queueMicrotask(() => root.unmount());
    },
    // Rides the block's own control strip instead of overlaying it.
    actions: [
      {
        title: "Open sketch",
        icon: PENCIL_RULER_SVG,
        run({ source }) {
          const { path } = parseSketchFence(source);
          if (path) host.openFile(path);
        },
      },
    ],
  };
}

export type { CodeBlockPreviews };
